import React from 'react';
import { Sun, Moon, Flame, Zap, Shield, Star } from 'lucide-react';

const YogiDagdhaDisplay = ({ yogiData }) => {
  if (!yogiData) return null;

  const yogiSystem = yogiData["6.1_yogi_avayogi_system"] || {};
  const dagdhaRasis = yogiData["6.2_dagdha_rasis"] || {};
  const trimurtiPlanets = yogiData["6.3_trimurti_planets"] || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-2">
          Yogi/Avayogi, Dagdha & Trimūrti Systems
        </h2>
        <p className="text-white/70 text-sm">
          Prosperity Indicators, Burnt Signs & Divine Trinity
        </p>
      </div>

      {/* Yogi/Avayogi System */}
      {yogiSystem.yogi_point && (
        <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 rounded-xl p-6 border border-yellow-500/30">
          <h3 className="text-yellow-400 text-2xl font-bold mb-6 flex items-center gap-2">
            <Sun className="w-6 h-6" /> Yogi / Avayogi System
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Yogi */}
            <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-green-400" />
                <h4 className="text-green-400 text-xl font-bold">Yogi Point</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Longitude:</span>
                  <span className="text-yellow-400 font-medium">{yogiSystem.yogi_point.longitude?.toFixed(2)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Sign:</span>
                  <span className="text-cyan-400 font-medium">{yogiSystem.yogi_point.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Nakṣatra:</span>
                  <span className="text-purple-400">{yogiSystem.yogi_point.nakshatra}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Pada:</span>
                  <span className="text-pink-400">{yogiSystem.yogi_point.pada}</span>
                </div>
              </div>
              
              <div className="mt-4 bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                <p className="text-white/80 text-xs mb-2 font-medium">Yogi Planet:</p>
                <p className="text-green-400 text-lg font-bold">{yogiSystem.yogi_planet?.planet}</p>
                <p className="text-white/60 text-xs mt-1 italic">{yogiSystem.yogi_planet?.meaning}</p>
              </div>
              
              <p className="text-white/50 text-xs mt-3 italic">{yogiSystem.yogi_point.meaning}</p>
            </div>

            {/* Avayogi */}
            <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 rounded-lg p-5 border border-red-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-5 h-5 text-red-400" />
                <h4 className="text-red-400 text-xl font-bold">Avayogi Point</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Longitude:</span>
                  <span className="text-yellow-400 font-medium">{yogiSystem.avayogi_point.longitude?.toFixed(2)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Sign:</span>
                  <span className="text-cyan-400 font-medium">{yogiSystem.avayogi_point.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Nakṣatra:</span>
                  <span className="text-purple-400">{yogiSystem.avayogi_point.nakshatra}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Pada:</span>
                  <span className="text-pink-400">{yogiSystem.avayogi_point.pada}</span>
                </div>
              </div>
              
              <div className="mt-4 bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                <p className="text-white/80 text-xs mb-2 font-medium">Avayogi Planet:</p>
                <p className="text-red-400 text-lg font-bold">{yogiSystem.avayogi_planet?.planet}</p>
                <p className="text-white/60 text-xs mt-1 italic">{yogiSystem.avayogi_planet?.meaning}</p>
              </div>
              
              <p className="text-white/50 text-xs mt-3 italic">{yogiSystem.avayogi_point.meaning}</p>
            </div>

            {/* Saha-Yogi (Duplicate Yogi) */}
            <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-lg p-5 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-blue-400" />
                <h4 className="text-blue-400 text-xl font-bold">Saha-Yogi (Duplicate Yogi)</h4>
              </div>
              
              <div className="mt-4 bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                <p className="text-white/80 text-xs mb-3 font-medium">Saha-Yogi Planet:</p>
                <p className="text-blue-400 text-2xl font-bold mb-3">{yogiSystem.saha_yogi_planet?.planet}</p>
                
                <div className="space-y-2 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-white/60">Based on:</span>
                    <span className="text-cyan-400 font-medium">Sign Lord of Yogi Point</span>
                  </div>
                  {yogiSystem.saha_yogi_planet?.sign_of_yogi_point && (
                    <div className="flex justify-between">
                      <span className="text-white/60">Yogi Point Sign:</span>
                      <span className="text-purple-400 font-medium">{yogiSystem.saha_yogi_planet.sign_of_yogi_point}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-white/60 text-xs italic">{yogiSystem.saha_yogi_planet?.meaning}</p>
              </div>
              
              <div className="mt-4 bg-white/5 rounded-lg p-3">
                <p className="text-white/50 text-xs">
                  <span className="font-bold text-blue-400">Note:</span> Saha Yogi is the ruler of the rashi (sign) where Yogi Point falls. It acts as a secondary benefic helper.
                </p>
              </div>
            </div>
          </div>

          {/* Interpretation */}
          {yogiSystem.interpretation && (
            <div className="mt-6 bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
              <p className="text-white/70 text-sm mb-2"><span className="font-bold">Note:</span> {yogiSystem.interpretation.note}</p>
              <p className="text-white/70 text-sm mb-2"><span className="font-bold">Usage:</span> {yogiSystem.interpretation.usage}</p>
              {yogiSystem.interpretation.formula && (
                <p className="text-white/60 text-xs mt-3 italic">
                  <span className="font-bold text-yellow-400">Formula:</span> {yogiSystem.interpretation.formula}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Dagdha Rasis */}
      {dagdhaRasis.weekday && (
        <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-6 border border-orange-500/30">
          <h3 className="text-orange-400 text-2xl font-bold mb-4 flex items-center gap-2">
            <Flame className="w-6 h-6" /> Dagdha Rāśis (Burnt Signs)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-2">Birth Weekday</p>
              <p className="text-yellow-400 text-2xl font-bold">{dagdhaRasis.weekday}</p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-2">Vara Lord (Day Ruler)</p>
              <p className="text-cyan-400 text-2xl font-bold">{dagdhaRasis.vara_lord}</p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-2">Lagna Sign</p>
              <p className="text-green-400 text-2xl font-bold">{dagdhaRasis.lagna_sign}</p>
            </div>
          </div>

          <div className="mt-6 bg-red-500/10 rounded-lg p-4 border border-red-500/30">
            <h4 className="text-red-400 font-bold mb-3">Burnt Signs for {dagdhaRasis.weekday}:</h4>
            <div className="flex gap-3">
              {dagdhaRasis.dagdha_signs?.map((sign, idx) => (
                <div key={idx} className="bg-red-900/50 px-4 py-2 rounded-lg border border-red-500/50">
                  <p className="text-red-300 font-bold text-lg">{sign}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`mt-4 rounded-lg p-4 border ${dagdhaRasis.lagna_is_dagdha ? 'bg-red-900/30 border-red-500/50' : 'bg-green-900/30 border-green-500/50'}`}>
            <p className="text-white font-bold mb-2">Lagna Status:</p>
            <p className={`text-lg font-bold ${dagdhaRasis.lagna_is_dagdha ? 'text-red-400' : 'text-green-400'}`}>
              {dagdhaRasis.lagna_is_dagdha ? '🔥 DAGDHA (Challenging)' : '✅ SAFE (Not Burnt)'}
            </p>
          </div>

          {dagdhaRasis.interpretation && (
            <div className="mt-4 bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
              <p className="text-white/70 text-sm">{dagdhaRasis.interpretation.lagna_status}</p>
            </div>
          )}
        </div>
      )}

      {/* Trimurti Planets */}
      {trimurtiPlanets.brahma && (
        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-xl p-6 border border-purple-500/30">
          <h3 className="text-purple-400 text-2xl font-bold mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6" /> Trimūrti Planets (Divine Trinity)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Brahma */}
            <div className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 rounded-lg p-5 border-2 border-yellow-500/50">
              <h4 className="text-yellow-400 text-xl font-bold mb-3">Brahmā</h4>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-white/60">Planet:</span>
                  <span className="text-cyan-400 font-bold text-lg">{trimurtiPlanets.brahma.planet}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Source:</span>
                  <span className="text-purple-400">{trimurtiPlanets.brahma.source}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Sign:</span>
                  <span className="text-green-400">{trimurtiPlanets.brahma.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">House:</span>
                  <span className="text-pink-400 font-bold">{trimurtiPlanets.brahma.house}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Strength:</span>
                  <span className="text-yellow-400 font-bold">{trimurtiPlanets.brahma.score?.toFixed(1)}</span>
                </div>
              </div>
              <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30">
                <p className="text-white/80 text-sm font-medium mb-1">{trimurtiPlanets.brahma.meaning}</p>
                <p className="text-white/60 text-xs italic mb-2">{trimurtiPlanets.brahma.role}</p>
                {trimurtiPlanets.brahma.method && (
                  <p className="text-white/50 text-xs border-t border-white/10 pt-2 mt-2">
                    <span className="font-bold">Method:</span> {trimurtiPlanets.brahma.method}
                  </p>
                )}
              </div>
            </div>

            {/* Mahesvara */}
            <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-lg p-5 border-2 border-blue-500/50">
              <h4 className="text-blue-400 text-xl font-bold mb-3">Maheśvara</h4>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-white/60">Planet:</span>
                  <span className="text-cyan-400 font-bold text-lg">{trimurtiPlanets.mahesvara.planet}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Source:</span>
                  <span className="text-purple-400">{trimurtiPlanets.mahesvara.source}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Sign:</span>
                  <span className="text-green-400">{trimurtiPlanets.mahesvara.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">House:</span>
                  <span className="text-pink-400 font-bold">{trimurtiPlanets.mahesvara.house}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Strength:</span>
                  <span className="text-yellow-400 font-bold">{trimurtiPlanets.mahesvara.score?.toFixed(1)}</span>
                </div>
              </div>
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                <p className="text-white/80 text-sm font-medium mb-1">{trimurtiPlanets.mahesvara.meaning}</p>
                <p className="text-white/60 text-xs italic mb-2">{trimurtiPlanets.mahesvara.role}</p>
                {trimurtiPlanets.mahesvara.method && (
                  <p className="text-white/50 text-xs border-t border-white/10 pt-2 mt-2">
                    <span className="font-bold">Method:</span> {trimurtiPlanets.mahesvara.method}
                  </p>
                )}
              </div>
            </div>

            {/* Rudra */}
            <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 rounded-lg p-5 border-2 border-red-500/50">
              <h4 className="text-red-400 text-xl font-bold mb-3">Rudra</h4>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-white/60">Planet:</span>
                  <span className="text-cyan-400 font-bold text-lg">{trimurtiPlanets.rudra.planet}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Source:</span>
                  <span className="text-purple-400">{trimurtiPlanets.rudra.source}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Sign:</span>
                  <span className="text-green-400">{trimurtiPlanets.rudra.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">House:</span>
                  <span className="text-pink-400 font-bold">{trimurtiPlanets.rudra.house}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Strength:</span>
                  <span className="text-yellow-400 font-bold">{trimurtiPlanets.rudra.score?.toFixed(1)}</span>
                </div>
              </div>
              <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                <p className="text-white/80 text-sm font-medium mb-1">{trimurtiPlanets.rudra.meaning}</p>
                <p className="text-white/60 text-xs italic mb-2">{trimurtiPlanets.rudra.role}</p>
                {trimurtiPlanets.rudra.method && (
                  <p className="text-white/50 text-xs border-t border-white/10 pt-2 mt-2">
                    <span className="font-bold">Method:</span> {trimurtiPlanets.rudra.method}
                  </p>
                )}
              </div>
            </div>
          </div>

          {trimurtiPlanets.interpretation && (
            <div className="mt-6 bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
              <p className="text-white/70 text-sm mb-2"><span className="font-bold">Note:</span> {trimurtiPlanets.interpretation.note}</p>
              <p className="text-white/70 text-sm"><span className="font-bold">Usage:</span> {trimurtiPlanets.interpretation.usage}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YogiDagdhaDisplay;
